/**
 * Application shell: wires the portal, the HUD and the 3D session together and
 * drives the render loop.
 */

import { SceneView } from './three/scene.js';
import { Session } from './game/session.js';
import { Auth, localProvider } from './auth/auth.js';
import { apiProvider, checkDbHealth, onApiStatus } from './auth/apiProvider.js';
import { realtime } from './net/realtime.js';
import { Sfx } from './audio/sfx.js';
import { preloadSamplePools } from './audio/samples.js';
import { createPiece } from './three/pieces.js';
import { loadParts, loadRig } from './three/parts.js';
import { initPortalTitleSweep } from './portalTitle.js';
import { medalElement } from './medals/render.js';
import { MEDALS, FAMILIES } from '../api/medals-catalogue.js';
import * as THREE from 'three';

// Independent of asset loading — the title is visible the instant the
// portal is, no need to wait on boot()'s model fetches.
initPortalTitleSweep();

const $ = (id) => document.getElementById(id);

const view = new SceneView($('stage'));
// A configured VITE_API_URL means a real backend is deployed alongside this
// build; otherwise fall back to the localStorage-only provider so `npm run
// dev` and an unconfigured deploy keep working exactly as before.
const auth = new Auth(import.meta.env.VITE_API_URL ? apiProvider : localProvider);
const sfx = new Sfx();
// Kicks off the drop-in-sample probe for every pool at boot, so the first
// select/attack/etc. of a real game doesn't pay for it — a no-op cost-wise
// when nothing has been dropped in (see public/audio/sfx/README.md).
preloadSamplePools(['select', 'confirm', 'attack', 'hurt'], ['male', 'queen']);

let session = null;
let lastOptions = null;
// The active networked match's id, so an incoming `move` can be routed to
// the right game and a locally-committed one tagged correctly going out —
// only ever one at a time, since a client is never in two matches at once.
let currentMatchId = null;

/* ------------------------------------------------------------------ */
/* Portal backdrop — a slow carousel of pieces behind the sign-in card  */
/* ------------------------------------------------------------------ */

const showcase = new THREE.Group();

/** Fills the carousel. Deferred until the geometry library has loaded. */
function populateShowcase() {
  const lineup = ['k', 'q', 'r', 'b', 'n', 'p'];
  lineup.forEach((type, i) => {
    const piece = createPiece(type, i % 2 === 0 ? 'w' : 'b');
    const angle = (i / lineup.length) * Math.PI * 2;
    piece.position.set(Math.cos(angle) * 2.6, 0, Math.sin(angle) * 2.6);
    piece.scale.setScalar(1.5);
    showcase.add(piece);
  });
}

{
  // The board's key light points at the board, so the showcase carries its own.
  const spot = new THREE.SpotLight(0xffe6bb, 90, 22, 0.62, 0.45, 1.6);
  spot.position.set(0, 9, 5.5);
  spot.target.position.set(0, 0.8, 0);
  spot.castShadow = true;
  spot.shadow.mapSize.set(1024, 1024);
  showcase.add(spot, spot.target);

  const fill = new THREE.PointLight(0x8fb4ff, 26, 16, 2);
  fill.position.set(-4.5, 3.2, -3.5);
  showcase.add(fill);

  const dais = new THREE.Mesh(
    new THREE.CylinderGeometry(3.5, 3.8, 0.3, 40),
    new THREE.MeshStandardMaterial({ color: 0x1b1e28, roughness: 0.85 })
  );
  dais.position.y = -0.15;
  dais.receiveShadow = true;
  showcase.add(dais);
  view.scene.add(showcase);
}

function setShowcaseVisible(on) {
  showcase.visible = on;
}

/* ------------------------------------------------------------------ */
/* Portal UI                                                           */
/* ------------------------------------------------------------------ */

let authMode = 'signin';

const setTab = (mode) => {
  authMode = mode;
  for (const tab of document.querySelectorAll('.tab')) {
    tab.classList.toggle('is-active', tab.dataset.tab === mode);
  }
  $('auth-submit').textContent = mode === 'signin' ? 'Enter the hall' : 'Take the oath';
  $('f-pass').setAttribute(
    'autocomplete',
    mode === 'signin' ? 'current-password' : 'new-password'
  );
  hideError();
};

const showError = (message) => {
  const el = $('auth-error');
  el.textContent = message;
  el.hidden = false;
};
const hideError = () => { $('auth-error').hidden = true; };

for (const tab of document.querySelectorAll('.tab')) {
  tab.addEventListener('click', () => setTab(tab.dataset.tab));
}

$('auth-form').addEventListener('submit', async (e) => {
  e.preventDefault();
  sfx.resume();
  hideError();
  const name = $('f-name').value.trim();
  const password = $('f-pass').value;
  const submit = $('auth-submit');
  submit.disabled = true;
  try {
    if (authMode === 'signin') await auth.signIn(name, password);
    else await auth.signUp(name, password);
    $('f-pass').value = '';
  } catch (err) {
    showError(err.message ?? 'Something went wrong.');
  } finally {
    submit.disabled = false;
  }
});

$('guest-btn').addEventListener('click', () => {
  sfx.resume();
  auth.playAsGuest();
});

$('signout-btn').addEventListener('click', () => auth.signOut());

/** Wires a segmented control and returns a getter for its current value. */
function segmented(id, initial) {
  const root = $(id);
  let value = initial;
  root.addEventListener('click', (e) => {
    const seg = e.target.closest('.seg');
    if (!seg) return;
    value = seg.dataset.value;
    for (const btn of root.querySelectorAll('.seg')) {
      btn.classList.toggle('is-active', btn === seg);
    }
    onOptionChange();
  });
  return () => value;
}

const getMode = segmented('opt-mode', 'ai');
const getDifficulty = segmented('opt-difficulty', 'knight');
const getSide = segmented('opt-side', 'w');

// A real backend turns "Two players" into "find one online" (see the
// dedicated click handler below) — there is then no local-hotseat path
// through the options panel/"March to battle" for it, so the button itself
// is hidden while that mode is selected. Without a backend this is always
// false and the panel behaves exactly as it always has.
const REALTIME_ENABLED = !!import.meta.env.VITE_API_URL;

function onOptionChange() {
  // Difficulty and side only mean anything against the computer.
  const vsAi = getMode() === 'ai';
  $('difficulty-wrap').hidden = !vsAi;
  $('side-wrap').hidden = !vsAi;
  $('play-btn').hidden = REALTIME_ENABLED && getMode() === 'hotseat';
}

auth.onChange((user) => {
  const signedIn = !!user;
  $('auth-view').hidden = signedIn;
  $('lobby-view').hidden = !signedIn;
  if (!signedIn) {
    realtime.disconnect();
    return;
  }
  $('lobby-name').textContent = user.name;
  const s = user.stats;
  $('lobby-record').textContent =
    `${s.wins}W · ${s.losses}L · ${s.draws}D  ·  ${s.games} played`;
  onOptionChange();
  if (!user.guest) {
    refreshRecentGames();
    // Presence/lobby/invites are for accounts only — a guest has nothing
    // stable to be invited back to, so it never connects.
    realtime.connect();
  } else {
    $('recent-games').hidden = true;
    realtime.disconnect();
  }
});

/* ---------------- the backend status light ---------------- */

// Nothing to report without a backend, so the LED stays hidden and no polling
// happens at all — the no-backend build behaves exactly as it did before.
if (import.meta.env.VITE_API_URL) {
  const led = $('db-led');
  const LABELS = {
    unknown: 'Checking the database…',
    busy: 'Reading the database…',
    online: 'Database online',
    offline: 'Database unreachable'
  };
  led.hidden = false;
  onApiStatus((state) => {
    led.classList.toggle('is-online', state === 'online');
    led.classList.toggle('is-busy', state === 'busy');
    led.classList.toggle('is-offline', state === 'offline');
    led.title = LABELS[state];
    led.setAttribute('aria-label', LABELS[state]);
  });

  checkDbHealth();
  // Re-checked only while the portal is actually on screen: a light nobody is
  // looking at is not worth a request every thirty seconds.
  setInterval(() => {
    if (!$('portal').hidden) checkDbHealth();
  }, 30000);
}

/** Only does anything with a real backend (`apiProvider.recentGames`); a
 * no-op with the default localStorage provider. The panel itself stays
 * visible whenever it can show *something* — an empty games list still
 * leaves the leaderboard toggle reachable, which is not conditional on
 * having played anything yourself. */
async function refreshRecentGames() {
  if (!auth.provider.recentGames) return;
  let games;
  try {
    games = await auth.provider.recentGames();
  } catch {
    return;
  }
  $('recent-games').hidden = false;
  $('recent-games-list').replaceChildren(...(games.length
    ? games.map((g) => {
      const li = document.createElement('li');
      const date = new Date(g.played_at).toLocaleDateString();
      li.innerHTML = `<span>${g.result}</span><span>${date}</span>`;
      return li;
    })
    : [Object.assign(document.createElement('li'), { textContent: 'No games yet.' })]));
}

/* ---------------- leaderboard toggle ---------------- */

let showingLeaderboard = false;

async function refreshLeaderboard() {
  if (!auth.provider.leaderboard) return;
  let rows;
  try {
    rows = await auth.provider.leaderboard();
  } catch {
    return;
  }
  $('leaderboard-list').replaceChildren(...rows.map((r, i) => buildLeaderboardRow(r, i)));
}

/**
 * One leaderboard row: rank, name, up to three medals, win count.
 *
 * Built element-wise rather than with innerHTML. The previous version
 * interpolated the player's own name into a template string, so a champion
 * who enlisted under a name containing a script tag ran code in the browser
 * of everyone who opened the leaderboard. textContent makes that impossible
 * rather than merely escaped.
 */
function buildLeaderboardRow(row, index) {
  const li = document.createElement('li');
  li.className = 'leaderboard-row';

  const rank = document.createElement('span');
  rank.className = 'leaderboard-rank';
  rank.textContent = `${index + 1}.`;

  const name = document.createElement('span');
  name.className = 'leaderboard-name';
  name.textContent = row.name;

  const wins = document.createElement('span');
  wins.className = 'leaderboard-wins';
  wins.textContent = `${row.wins}W`;

  li.append(rank, name);

  if (row.medals?.length) {
    // One button for the whole strip rather than one per medal: at 14px the
    // individual hit targets would be unusable, and they all lead to the
    // same cabinet anyway.
    const strip = document.createElement('button');
    strip.type = 'button';
    strip.className = 'leaderboard-medals';
    strip.setAttribute('aria-label', `View ${row.name}'s medal cabinet`);
    for (const medal of row.medals) {
      strip.append(medalElement(medal.medal_id, {
        tier: medal.tier,
        online: medal.online,
        size: 14
      }));
    }
    strip.addEventListener('click', () => openCabinet(row));
    li.append(strip);
  }

  li.append(wins);
  return li;
}

/* ---------------- medal cabinet ---------------- */

/**
 * Opens the cabinet for one player.
 *
 * The leaderboard only carries each player's three rarest medals, which is
 * all a row has space for — so for anyone else's cabinet that is all there
 * is to show, and the rest are drawn locked. Viewing your own pulls the
 * complete set from `/api/medals` first.
 */
async function openCabinet(row) {
  const own = auth.user && !auth.user.guest && row.name === auth.user.name;
  let held = row.medals ?? [];

  if (own && auth.provider.medals) {
    try {
      held = await auth.provider.medals();
    } catch {
      /* Falls back to the three from the leaderboard row. */
    }
  }

  $('cabinet-title').textContent = own ? 'Your medal cabinet' : `${row.name}'s medals`;
  $('cabinet-subtitle').textContent = own
    ? `${held.length} of ${MEDALS.length} earned.`
    : 'Only a champion sees their own full cabinet — these are the ones they show.';
  $('cabinet-body').replaceChildren(...buildCabinet(held));
  $('medal-cabinet').hidden = false;
}

/** The cabinet's contents: every medal in the game, grouped by family. */
function buildCabinet(held) {
  const byId = new Map(held.map((m) => [m.medal_id, m]));

  return FAMILIES.map((family) => {
    const section = document.createElement('section');
    section.className = 'cabinet-family';

    const heading = document.createElement('h4');
    heading.textContent = family.name;
    const blurb = document.createElement('p');
    blurb.className = 'cabinet-blurb';
    blurb.textContent = family.blurb;
    section.append(heading, blurb);

    const grid = document.createElement('div');
    grid.className = 'cabinet-grid';

    for (const def of MEDALS.filter((m) => m.family === family.id)) {
      const mine = byId.get(def.id);
      const earned = !!mine;
      const tier = mine?.tier ?? 0;

      const item = document.createElement('div');
      item.className = `cabinet-medal${earned ? '' : ' is-locked'}`;
      item.append(medalElement(def, { tier, earned, online: mine?.online, size: 38 }));

      const name = document.createElement('span');
      name.className = 'cabinet-medal-name';
      name.textContent = def.name;

      const note = document.createElement('span');
      note.className = 'cabinet-medal-note';
      // Earned and tiered: name the tier reached and what the next one asks
      // for. Earned and binary: just say so. Unearned: show the bar, since
      // the whole point of a visible locked medal is to be a target.
      if (earned && def.tiers.length > 1) {
        const next = def.tiers[tier + 1];
        note.textContent = next
          ? `${def.tiers[tier].label} · next at ${next.at}`
          : `${def.tiers[tier].label} · complete`;
      } else if (earned) {
        note.textContent = 'Earned';
      } else {
        note.textContent = def.tiers.length > 1
          ? `Locked · from ${def.tiers[0].at}`
          : 'Locked';
      }

      item.append(name, note);
      item.title = def.description;
      grid.append(item);
    }

    section.append(grid);
    return section;
  });
}

$('cabinet-close').addEventListener('click', () => { $('medal-cabinet').hidden = true; });
$('medal-cabinet').addEventListener('click', (e) => {
  // Clicking the backdrop closes; clicking the card itself must not.
  if (e.target === $('medal-cabinet')) $('medal-cabinet').hidden = true;
});

$('toggle-leaderboard').addEventListener('click', async () => {
  showingLeaderboard = !showingLeaderboard;
  $('recent-games-label').textContent = showingLeaderboard ? 'Leaderboard' : 'Recent games';
  $('toggle-leaderboard').textContent = showingLeaderboard ? 'Recent games' : 'Leaderboard';
  $('recent-games-list').hidden = showingLeaderboard;
  $('leaderboard-list').hidden = !showingLeaderboard;
  if (showingLeaderboard) refreshLeaderboard();
});

/* ---------------- presence, lobby and invites ---------------- */

realtime.on('presence', ({ online, playing }) => {
  const el = $('presence-counts');
  el.hidden = false;
  el.textContent = `${online} online · ${playing} playing`;
});

function renderLobbyList(players) {
  const mine = auth.user?.name;
  $('lobby-empty').hidden = players.length > 0;
  $('lobby-list').replaceChildren(...players
    .filter((p) => p.name !== mine)
    .map((p) => {
      const li = document.createElement('li');
      const name = document.createElement('span');
      name.className = 'lobby-name';
      name.textContent = p.name;
      const record = document.createElement('span');
      record.className = 'lobby-record';
      record.textContent = `${p.wins}W · ${p.losses}L`;
      const btn = document.createElement('button');
      btn.className = 'invite-btn';
      btn.type = 'button';
      btn.textContent = 'Invite';
      btn.addEventListener('click', () => {
        btn.disabled = true;
        btn.textContent = 'Invited…';
        realtime.send('invite', { to: p.name, message: `${mine} wants to play with you.` });
      });
      li.append(name, record, btn);
      return li;
    }));
}

realtime.on('lobby_list', ({ players }) => renderLobbyList(players));

function lobbyStatus(text) {
  const el = $('lobby-status');
  el.hidden = false;
  el.textContent = text;
  setTimeout(() => { el.hidden = true; }, 4000);
}

realtime.on('invite_failed', ({ to }) => lobbyStatus(`${to} is no longer available.`));
realtime.on('invite_declined', ({ by }) => lobbyStatus(`${by} declined.`));

function openLobby() {
  sfx.resume();
  $('lobby-overlay').hidden = false;
  realtime.send('status', { status: 'lobby' });
}

function closeLobby() {
  $('lobby-overlay').hidden = true;
  realtime.send('status', { status: 'idle' });
}

$('lobby-close').addEventListener('click', closeLobby);

let pendingInviter = null;

realtime.on('invite_received', ({ from, message }) => {
  pendingInviter = from;
  $('invite-title').textContent = `${from} wants to play`;
  $('invite-message').textContent = message || 'Do you wish to play?';
  $('invite-modal').hidden = false;
});

$('invite-accept').addEventListener('click', () => {
  sfx.resume();
  $('invite-modal').hidden = true;
  realtime.send('invite_response', { from: pendingInviter, accept: true });
});
$('invite-decline').addEventListener('click', () => {
  $('invite-modal').hidden = true;
  realtime.send('invite_response', { from: pendingInviter, accept: false });
});

realtime.on('match_start', ({ matchId, color, opponent }) => {
  $('lobby-overlay').hidden = true;
  $('invite-modal').hidden = true;
  currentMatchId = matchId;
  // The match id rides on the session rather than only in `currentMatchId`,
  // which the abandonment paths clear before the result is reported — and
  // it is now what identifies the game to the server.
  startGame({ mode: 'remote', playerColor: color, opponentName: opponent, matchId });
});

realtime.on('move', (msg) => {
  if (msg.matchId !== currentMatchId || session?.options.mode !== 'remote') return;
  session.applyRemoteMove(msg);
});

realtime.on('opponent_left', ({ matchId }) => {
  if (matchId !== currentMatchId) return;
  currentMatchId = null;
  // An opponent who walks out has resigned, so this is a win and is recorded
  // as one — previously the game simply vanished, taking any medals earned
  // in it with it.
  if (session && !session.finished) {
    session.finished = true;
    reportResult('win');
  }
  $('opponent-left').hidden = false;
});
$('opponent-left-portal').addEventListener('click', () => {
  $('opponent-left').hidden = true;
  showPortal();
});

$('play-btn').addEventListener('click', () => {
  sfx.resume();
  startGame({
    mode: getMode(),
    difficulty: getDifficulty(),
    playerColor: getSide()
  });
});

// With a real backend, "Two players" opens the online lobby directly instead
// of just selecting a local mode — see REALTIME_ENABLED above. Without one,
// `segmented()`'s own delegated listener already gives today's exact
// behavior (select hotseat, wait for "March to battle"), untouched.
document.querySelector('#opt-mode .seg[data-value="hotseat"]')
  ?.addEventListener('click', () => {
    if (REALTIME_ENABLED) openLobby();
  });

/* ------------------------------------------------------------------ */
/* Screens                                                             */
/* ------------------------------------------------------------------ */

function showPortal() {
  $('portal').hidden = false;
  $('hud').hidden = true;
  $('gameover').hidden = true;
  $('promotion').hidden = true;
  $('opponent-left').hidden = true;
  setShowcaseVisible(true);
  view.targetRadius = 11;
  view.targetPolar = 1.05;
  if (session) {
    // Leaving an unfinished networked game mid-play is a resignation as far
    // as the opponent is concerned — without this they'd just wait on a
    // socket that's still technically open (a tab navigating away isn't a
    // disconnect the server can see on its own).
    if (session.options.mode === 'remote' && !session.finished && currentMatchId) {
      realtime.send('resign', { matchId: currentMatchId });
      currentMatchId = null;
      // Own it as a loss rather than letting the game go unrecorded: the
      // opponent is being told they won, and a result that counts for one
      // side has to count for the other. Fired without awaiting — the portal
      // should not wait on the network to appear.
      session.finished = true;
      reportResult('loss');
    }
    session.dispose();
    session = null;
  }
}

function showBoard() {
  $('portal').hidden = true;
  $('hud').hidden = false;
  setShowcaseVisible(false);
  view.targetRadius = 13.5;
  view.targetPolar = 0.95;
}

/* ------------------------------------------------------------------ */
/* HUD                                                                 */
/* ------------------------------------------------------------------ */

function resetHud() {
  $('movelist').replaceChildren();
  $('taken-w').replaceChildren();
  $('taken-b').replaceChildren();
  $('banner').hidden = true;
  $('hud-undo').disabled = true;
  $('go-again').hidden = false;
}

function appendMove(san, turnAfter) {
  const list = $('movelist');
  // `turnAfter` is the side to move *after* this ply, so White just moved
  // whenever it is now Black's turn.
  const whiteJustMoved = turnAfter === 'b';
  if (whiteJustMoved) {
    const li = document.createElement('li');
    const num = document.createElement('span');
    num.textContent = `${list.children.length + 1}.`;
    const white = document.createElement('span');
    white.textContent = san;
    li.append(num, white, document.createElement('span'));
    list.append(li);
  } else {
    const li = list.lastElementChild;
    if (li) li.lastElementChild.textContent = san;
  }
  list.scrollTop = list.scrollHeight;
}

function setTurnPlate({ turn, inCheck, yours }) {
  $('turn-dot').classList.toggle('is-dark', turn === 'b');
  const side = turn === 'w' ? 'Light' : 'Dark';
  const who = session?.options.mode === 'hotseat' ? '' : yours ? ' (you)' : '';
  $('turn-text').textContent = `${side}${who} to move`;
  $('banner').hidden = !inCheck;
  if (inCheck) $('banner').textContent = 'Check!';
}

function startGame(options) {
  lastOptions = options;
  showBoard();
  resetHud();
  if (session) session.dispose();

  session = new Session(view, options, {
    // Impacts route through the clusterer so several landing together become
    // one crash; everything else plays straight.
    onSound: (cue, opts) => (opts?.sample
      ? sfx.voiceSample(opts.sample, opts.pool, cue, opts)
      : opts?.impact ? sfx.impact(cue, opts) : sfx.play(cue, opts)),
    onTurn: (state) => {
      setTurnPlate(state);
      $('hud-undo').disabled = !session || session.game.history.length === 0
        || session.options.mode === 'remote';
    },
    onMove: ({ san, turn }) => appendMove(san, turn),
    onLocalMove: ({ from, to, promotion }) => {
      realtime.send('move', { matchId: currentMatchId, from, to, promotion });
    },
    onCapture: ({ color, glyph }) => {
      const span = document.createElement('span');
      span.textContent = glyph;
      $(`taken-${color}`).append(span);
    },
    onPromotion: (choose) => {
      $('promotion').hidden = false;
      const handler = (e) => {
        const btn = e.target.closest('.promo');
        if (!btn) return;
        $('promotion').hidden = true;
        $('promotion').removeEventListener('click', handler);
        choose(btn.dataset.promo);
      };
      $('promotion').addEventListener('click', handler);
    },
    onRebuild: ({ history, captured }) => {
      resetHud();
      // Replay the move list into the HUD; `turn` alternates from White.
      history.forEach((san, i) => appendMove(san, i % 2 === 0 ? 'b' : 'w'));
      for (const [color, glyphs] of Object.entries(captured)) {
        for (const glyph of glyphs) {
          const span = document.createElement('span');
          span.textContent = glyph;
          $(`taken-${color}`).append(span);
        }
      }
    },
    onStatus: (outcome) => showGameOver(outcome)
  });
}

/**
 * Reports a finished game — the one place a result reaches the backend.
 *
 * Every way a game can end routes through here: checkmate and the draws via
 * `showGameOver`, walking out of a live match, and an opponent walking out
 * of one. Those last two used to record nothing at all, so an abandoned
 * online game left no trace and awarded no medals.
 *
 * Marks the session finished so the same game can never be reported twice
 * (leaving the portal after a checkmate would otherwise file it again).
 */
async function reportResult(result) {
  if (!session || session.reported) return;
  session.reported = true;

  const { mode, playerColor, opponentName, matchId } = session.options;

  // Hotseat is played and recorded but never counted. Both armies belong to
  // whoever is at the keyboard, so there is no "you" in the game to credit —
  // and `checkEnd` reports every hotseat checkmate as a win regardless of
  // which side won, which made a four-ply Fool's Mate on repeat the cheapest
  // route up the leaderboard. The server enforces this too; skipping it here
  // only keeps the portal from showing a win it is about to take back.
  if (mode !== 'hotseat') await auth.recordResult(result);

  // Optional: only `apiProvider` implements saveGame, so this is a no-op
  // with the default localStorage provider (no backend configured).
  if (!auth.user || auth.user.guest) return;

  // Colour and mode are what let the server tell this player's captures
  // from their opponent's when it awards medals. For an online game the
  // match id is the whole identity of the report: the server reads the
  // opponent and the colour out of its own record of that match and
  // ignores what is sent here, so there is nothing to gain by lying.
  const saved = await auth.provider.saveGame?.(result, session.game.history, {
    color: mode === 'hotseat' ? null : playerColor,
    mode,
    matchId: mode === 'remote' ? matchId : undefined,
    opponent: mode === 'remote' ? opponentName : undefined
  })?.catch(() => null);
  // Recording the game is what moves the tally now, so the server's figures
  // outrank the optimistic increment `recordResult` made above.
  if (saved?.stats) auth.adoptStats(saved.stats);
  refreshRecentGames();
}

async function showGameOver({ status, winner, result }) {
  await reportResult(result);

  const titles = {
    checkmate: 'Checkmate',
    stalemate: 'Stalemate',
    'fifty-move': 'Drawn',
    insufficient: 'Drawn'
  };
  const side = winner === 'w' ? 'Light' : 'Dark';
  const details = {
    checkmate: `${side} takes the field. The ${winner === 'w' ? 'dark' : 'light'} king has fallen.`,
    stalemate: 'No legal move remains, yet no king is in check.',
    'fifty-move': 'Fifty moves have passed without a capture or a pawn advance.',
    insufficient: 'Neither army has the strength left to force a mate.'
  };

  $('go-title').textContent = titles[status] ?? 'Game over';
  $('go-detail').textContent = details[status] ?? '';
  // Guests earn nothing (reportResult stops before the backend), and hotseat
  // is played for its own sake — neither is worth a nudge unless there is a
  // backend to enlist with in the first place.
  $('go-guest').hidden = !(REALTIME_ENABLED && auth.user?.guest && session.options.mode !== 'hotseat');
  // "Fight again" would try to reconnect with a match that's already over —
  // a finished networked game only ever goes back to the portal, where a
  // fresh invite starts a genuinely new match.
  const isRemote = session.options.mode === 'remote';
  $('go-again').hidden = isRemote;
  if (isRemote) {
    currentMatchId = null;
    realtime.send('status', { status: 'idle' });
  }
  $('gameover').hidden = false;
}

$('go-again').addEventListener('click', () => {
  $('gameover').hidden = true;
  startGame(lastOptions);
});
$('go-portal').addEventListener('click', showPortal);
$('hud-back').addEventListener('click', showPortal);
$('hud-flip').addEventListener('click', () => {
  view.targetAzimuth += Math.PI;
});
$('hud-undo').addEventListener('click', () => {
  if (session?.undo()) sfx.play('select');
});

const soundBtn = $('hud-sound');
soundBtn.addEventListener('click', () => {
  const on = soundBtn.getAttribute('aria-pressed') !== 'true';
  soundBtn.setAttribute('aria-pressed', String(on));
  sfx.setEnabled(on);
  if (on) sfx.play('select');
});

/* ------------------------------------------------------------------ */
/* Skipping a battle                                                    */
/* ------------------------------------------------------------------ */

const skipBtn = $('hud-skip');

/** Fast-forwards the fight in progress. Harmless when nothing is playing. */
function skipBattle() {
  if (!session?.animator.busy) return false;
  session.animator.skip();
  return true;
}

skipBtn.addEventListener('click', skipBattle);
// A press anywhere on the board skips too — the button is only a hint that it
// is possible. Bound to pointerdown so the release still lands as a move.
view.onPointerDown = skipBattle;

// Playing to an empty room helps nobody, and a battle running in a hidden tab
// would otherwise keep making noise.
document.addEventListener('visibilitychange', () => sfx.setHidden(document.hidden));

document.addEventListener('keydown', (e) => {
  if ($('hud').hidden) return;
  if (e.key === 'Escape') showPortal();
  if (e.key === 'f') view.targetAzimuth += Math.PI;
  if (e.key === ' ') {
    // Space would otherwise re-trigger whichever HUD button has focus.
    if (skipBattle()) e.preventDefault();
  }
});

/* ------------------------------------------------------------------ */
/* Render loop                                                         */
/* ------------------------------------------------------------------ */

const clock = new THREE.Clock();
function loop() {
  requestAnimationFrame(loop);
  const dt = Math.min(clock.getDelta(), 0.05); // clamp after a tab switch
  const elapsed = clock.getElapsedTime();

  if (showcase.visible) showcase.rotation.y += dt * 0.12;
  session?.update(dt, elapsed);
  skipBtn.hidden = !session?.animator.busy;
  view.update(dt, elapsed);
  view.render();
}

/**
 * Debug handle. Exposed deliberately so the game can be driven from the
 * console or an end-to-end test without clicking through the 3D scene:
 *
 *   __battlechess.start({ mode: 'hotseat' })
 *   __battlechess.play('e2', 'e4')
 */
window.__battlechess = {
  get session() { return session; },
  view,
  auth,
  start: startGame,
  /** Plays a move by square name, e.g. play('g1', 'f3'). */
  play(from, to, promotion) {
    if (!session) return 'no game in progress';
    const parse = (name) =>
      (8 - Number(name[1])) * 8 + 'abcdefgh'.indexOf(name[0]);
    const move = session.game
      .legalMoves()
      .find((m) => m.from === parse(from) && m.to === parse(to) &&
        (promotion ? m.promotion === promotion : true));
    if (!move) return 'illegal';
    session.execute(move);
    return 'ok';
  },
  get busy() { return !!session?.animator.busy; }
};

/**
 * Boot. The Blender geometry library has to be in memory before the first
 * piece is built, so nothing that calls `createPiece` runs until it resolves.
 * BASE_URL keeps the path right under a sub-path deployment (see APP_BASE).
 */
async function boot() {
  // The shared shape library, and the one piece that is a sculpted rig rather
  // than an assembly of library parts. Fetched together; either failing leaves
  // the game on primitives rather than stopping it.
  await Promise.all([
    loadParts(`${import.meta.env.BASE_URL}models/pieces.glb`),
    loadRig('knight', `${import.meta.env.BASE_URL}models/knight.glb`),
    loadRig('bishop', `${import.meta.env.BASE_URL}models/bishop.glb`),
    loadRig('king', `${import.meta.env.BASE_URL}models/king.glb`),
    loadRig('queen', `${import.meta.env.BASE_URL}models/queen.glb`),
    // Not a figure: the pawn's sword, shield and three helmets share one file.
    loadRig('props', `${import.meta.env.BASE_URL}models/props.glb`)
  ]);
  populateShowcase();
  showPortal();
  onOptionChange();
  loop();
}

boot();
